import React, { useCallback, useEffect, useRef, useState } from 'react';
import comms from '../../comms';
import {
  PASSENGER_DETERMINED_DROP,
  isMissingDropoffAddressText,
  normalizeMapLatLng,
  isValidMapPosition,
  readGoogleLatLng,
} from './locationHelper';

// EditTripModal — lets the driver amend an in-progress booking (#1 Edit Trip).
//
// Shown only for trips the driver is allowed to change: flag trips (which arrive
// with no destination — "Passenger Determined") and cash / credit-card trips
// (no account billing). Account-billed trips are NOT editable here, so the
// caller hides the entry point for them.
//
// The driver can set/replace the destination (resolved to lat/lon via Google
// Places → Geocoder fallback, mirroring CurrentWaypoint's route editor), the
// passenger count, the fare and a free-text note. On Save we diff against the
// known baseline and send ONLY the changed fields through comms.updateTrip,
// which:
//   • in mock mode echoes the change back (fully testable offline), and
//   • live POSTs /API/Trips/Update with the fleet apiKey — and throws a clear,
//     user-facing error if that key is not configured (we surface it inline).
//
// NOTE (backend dependency): the live waypoint carries only `jobNumber`, not the
// trip GUID that /API/Trips/Update expects. We send the best identifier we have
// (jobNumber) and rely on the server to resolve it; if the SaaS requires the
// GUID, JOBM/JOBMF must start carrying the trip Id. This is called out in the
// task report rather than guessed.

function resolveTripId(waypoint) {
  return (
    waypoint?.id ||
    waypoint?.Id ||
    waypoint?.tripId ||
    waypoint?.TripId ||
    waypoint?.tripNumber ||
    waypoint?.jobNumber ||
    null
  );
}

function EditTripModal({ isOpen, waypoint, center, onClose, onSaved }) {
  const isFlagAwaitingDestination =
    waypoint?.type === 'Dropoff' &&
    isMissingDropoffAddressText(waypoint?.address1) &&
    Array.isArray(waypoint?.location) &&
    Number(waypoint.location[0]) === 0 &&
    Number(waypoint.location[1]) === 0;

  // Baseline destination label (blank for a flag trip with no destination yet).
  const baselineDestination =
    waypoint?.type === 'Dropoff' &&
    !isMissingDropoffAddressText(waypoint?.address1) &&
    waypoint?.address1 !== PASSENGER_DETERMINED_DROP
      ? String(waypoint.address1 || '').trim()
      : '';

  const [destinationText, setDestinationText] = useState('');
  const [resolvedDestination, setResolvedDestination] = useState(null); // { lat, lng, label }
  const [passengerCount, setPassengerCount] = useState('');
  const [fare, setFare] = useState('');
  const [note, setNote] = useState('');

  const [predictions, setPredictions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const placeIdRef = useRef(null);
  const pickedDescRef = useRef('');
  const debounceRef = useRef(null);

  // Reset the form each time the modal opens for a (possibly different) waypoint.
  useEffect(() => {
    if (!isOpen) return;
    setDestinationText(baselineDestination);
    setResolvedDestination(null);
    setPassengerCount('');
    setFare('');
    setNote('');
    setPredictions([]);
    setError(null);
    setDone(false);
    setSubmitting(false);
    placeIdRef.current = null;
    pickedDescRef.current = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, waypoint?.jobNumber, waypoint?.type]);

  const fetchPredictions = useCallback(
    (input) => {
      const trimmed = String(input || '').trim();
      if (trimmed.length < 2 || !window.google?.maps?.places?.AutocompleteService) {
        setPredictions([]);
        return;
      }
      const svc = new window.google.maps.places.AutocompleteService();
      const request = { input: trimmed };
      const biased = normalizeMapLatLng(center);
      if (biased && isValidMapPosition(biased)) {
        request.location = new window.google.maps.LatLng(biased.lat, biased.lng);
        request.radius = 50000;
      }
      svc.getPlacePredictions(request, (preds, status) => {
        const OK = window.google.maps.places.PlacesServiceStatus.OK;
        const ZERO = window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS;
        if (status !== OK && status !== ZERO) {
          setPredictions([]);
          return;
        }
        setPredictions(preds || []);
      });
    },
    [center]
  );

  // Debounced autocomplete as the driver types.
  useEffect(() => {
    if (!isOpen) return undefined;
    const trimmed = String(destinationText || '').trim();
    if (trimmed.length < 2 || (placeIdRef.current && trimmed === pickedDescRef.current)) {
      setPredictions([]);
      return undefined;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      fetchPredictions(trimmed);
    }, 280);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [isOpen, destinationText, fetchPredictions]);

  const handleDestinationChange = (value) => {
    placeIdRef.current = null;
    pickedDescRef.current = '';
    setResolvedDestination(null);
    setDestinationText(value);
  };

  const handlePickPrediction = (prediction) => {
    if (!prediction?.description || !prediction.place_id) return;
    placeIdRef.current = prediction.place_id;
    pickedDescRef.current = prediction.description;
    setResolvedDestination(null);
    setDestinationText(prediction.description);
    setPredictions([]);
    setError(null);
  };

  // Resolve the typed/picked destination to coordinates: Places getDetails first,
  // then a Geocoder fallback (same strategy as CurrentWaypoint's route editor).
  const resolveDestination = useCallback(async () => {
    const text = String(destinationText || '').trim();
    if (!text) return null;

    const placeId = placeIdRef.current;
    if (placeId && window.google?.maps?.places?.PlacesService) {
      const details = await new Promise((resolve) => {
        const svc = new window.google.maps.places.PlacesService(document.createElement('div'));
        svc.getDetails(
          { placeId, fields: ['geometry', 'formatted_address', 'name'] },
          (place, status) => {
            const OK = window.google.maps.places.PlacesServiceStatus.OK;
            resolve(status === OK && place?.geometry?.location ? place : null);
          }
        );
      });
      const loc = readGoogleLatLng(details?.geometry?.location);
      if (loc && isValidMapPosition(loc)) {
        return { lat: loc.lat, lng: loc.lng, label: String(details?.formatted_address || text).trim() };
      }
    }

    if (window.google?.maps?.Geocoder) {
      const geocoder = new window.google.maps.Geocoder();
      const biased = normalizeMapLatLng(center);
      const request = { address: text };
      if (biased && isValidMapPosition(biased)) {
        const pad = 0.6;
        request.bounds = new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(biased.lat - pad, biased.lng - pad),
          new window.google.maps.LatLng(biased.lat + pad, biased.lng + pad)
        );
      }
      const results = await new Promise((resolve) => {
        geocoder.geocode(request, (res, status) => {
          resolve(status === window.google.maps.GeocoderStatus.OK && res?.length ? res : []);
        });
      });
      const first = results?.[0];
      const loc = readGoogleLatLng(first?.geometry?.location);
      if (loc && isValidMapPosition(loc)) {
        return { lat: loc.lat, lng: loc.lng, label: String(first?.formatted_address || text).trim() };
      }
    }
    return null;
  }, [destinationText, center]);

  const handleSubmit = async () => {
    setError(null);
    const text = String(destinationText || '').trim();
    const destinationChanged = text !== baselineDestination;

    // A flag trip MUST end up with a destination before it can be priced.
    if (isFlagAwaitingDestination && !text) {
      setError('Enter the destination for this flag trip.');
      return;
    }

    const changes = {};
    let destLatLng = null;

    if (text && destinationChanged) {
      destLatLng = resolvedDestination || (await resolveDestination());
      if (!destLatLng) {
        setError('Could not locate that destination. Try a fuller address.');
        return;
      }
      changes.destination = destLatLng.label || text;
      changes.destinationLat = destLatLng.lat;
      changes.destinationLng = destLatLng.lng;
    }

    const paxNum = Number(passengerCount);
    if (passengerCount !== '' && Number.isFinite(paxNum) && paxNum > 0) {
      changes.passengerCount = paxNum;
    }
    const fareNum = Number(fare);
    if (fare !== '' && Number.isFinite(fareNum) && fareNum >= 0) {
      changes.fare = fareNum;
    }
    if (note.trim()) {
      changes.notes = note.trim();
    }

    if (Object.keys(changes).length === 0) {
      setError('Change at least one field before saving.');
      return;
    }

    const tripId = resolveTripId(waypoint);
    if (!tripId) {
      setError('This trip has no identifier to update. Please contact dispatch.');
      return;
    }

    setSubmitting(true);
    try {
      await comms.updateTrip(tripId, changes);
      setDone(true);
      if (typeof onSaved === 'function') onSaved(tripId, changes, destLatLng);
      setTimeout(() => {
        setDone(false);
        if (typeof onClose === 'function') onClose();
      }, 900);
    } catch (e) {
      // comms.updateTrip throws a clear message when the fleet apiKey is missing.
      setError(e?.message || 'Could not save changes. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[2200] flex items-end justify-center bg-black/40 sm:items-center"
      onClick={() => {
        if (!submitting) onClose?.();
      }}
    >
      <div
        className="w-full max-w-md rounded-t-2xl bg-white p-4 shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">
            {isFlagAwaitingDestination ? 'Add destination & fare' : 'Edit trip'}
          </h3>
          <button
            type="button"
            onClick={() => onClose?.()}
            disabled={submitting}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 disabled:opacity-50"
          >
            ✕
          </button>
        </div>

        {waypoint?.jobNumber && (
          <p className="mb-3 text-xs text-gray-500">
            Trip #{waypoint.jobNumber}
            {waypoint.paxName ? ` · ${waypoint.paxName}` : ''}
          </p>
        )}

        {/* Destination */}
        <label className="mb-1 block text-xs font-medium text-gray-500">
          Destination {isFlagAwaitingDestination && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <input
            type="text"
            autoComplete="off"
            value={destinationText}
            onChange={(e) => handleDestinationChange(e.target.value)}
            placeholder="Start typing an address or place"
            className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-blue-600"
          />
          {predictions.length > 0 && (
            <div className="absolute left-0 right-0 top-full z-30 mt-0.5 rounded-xl border border-gray-200 bg-white shadow-md">
              <ul className="max-h-48 overflow-y-auto" role="listbox">
                {predictions.map((p) => (
                  <li key={p.place_id} className="border-b border-gray-100 last:border-b-0">
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handlePickPrediction(p)}
                    >
                      <span className="font-medium text-gray-900">
                        {p.structured_formatting?.main_text || p.description}
                      </span>
                      {p.structured_formatting?.secondary_text && (
                        <span className="block text-xs text-gray-600">
                          {p.structured_formatting.secondary_text}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Passenger count + fare */}
        <div className="mt-3 flex gap-3">
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-gray-500">Passengers</label>
            <input
              type="number"
              inputMode="numeric"
              min="1"
              step="1"
              value={passengerCount}
              onChange={(e) => setPassengerCount(e.target.value)}
              placeholder="—"
              className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-blue-600"
            />
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-gray-500">Fare</label>
            <div className="flex items-center rounded-xl border border-gray-300 px-3 focus-within:border-blue-600">
              <span className="text-sm font-semibold text-gray-400">$</span>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={fare}
                onChange={(e) => setFare(e.target.value)}
                placeholder="0.00"
                className="w-full bg-transparent px-2 py-2.5 text-sm font-semibold text-gray-900 outline-none"
              />
            </div>
          </div>
        </div>

        {/* Note */}
        <label className="mb-1 mt-3 block text-xs font-medium text-gray-500">Note (optional)</label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Extra stop, waiting time"
          className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-blue-600"
        />

        {error && (
          <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
        )}
        {done && (
          <div className="mt-3 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700">
            Trip updated.
          </div>
        )}

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => onClose?.()}
            disabled={submitting}
            className="flex-1 rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || done}
            className="flex-1 rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {submitting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditTripModal;
